/**
 * Error Monitoring and Analytics
 * 
 * Production error tracking with Sentry integration.
 * Configure VITE_SENTRY_DSN environment variable to enable Sentry.
 */

import * as Sentry from "@sentry/react";

interface ErrorContext {
  userId?: string;
  teamId?: string;
  component?: string;
  action?: string;
  [key: string]: any;
}

class Monitoring {
  private initialized = false;

  init() {
    if (this.initialized) return;

    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (dsn && import.meta.env.PROD) {
      Sentry.init({
        dsn,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE,
        beforeSend(event) {
          // Filter out expected errors
          if (event.exception?.values?.[0]?.type === 'AuthApiError') {
            return null;
          }
          return event;
        },
      });
    }

    // Development error tracking
    if (import.meta.env.DEV) {
      window.addEventListener('error', (event) => {
        console.error('[Error]', {
          message: event.message,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          error: event.error,
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        console.error('[Unhandled Rejection]', event.reason);
      });
    }

    this.initialized = true;
  }

  captureError(error: Error, context?: ErrorContext) {
    if (import.meta.env.DEV) {
      console.error('[Captured Error]', error, context);
    }

    if (this.initialized && import.meta.env.PROD) {
      Sentry.captureException(error, {
        contexts: { additional: context },
      });
    }
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext) {
    if (import.meta.env.DEV) {
      console.log(`[${level.toUpperCase()}]`, message, context);
    }

    if (this.initialized && import.meta.env.PROD) {
      Sentry.captureMessage(message, {
        level,
        contexts: { additional: context },
      });
    }
  }

  setUser(user: { id: string; email?: string; teamId?: string }) {
    if (this.initialized && import.meta.env.PROD) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        teamId: user.teamId,
      });
    }
  }

  clearUser() {
    if (this.initialized && import.meta.env.PROD) {
      Sentry.setUser(null);
    }
  }

  trackEvent(eventName: string, properties?: Record<string, any>) {
    if (import.meta.env.DEV) {
      console.log('[Analytics Event]', eventName, properties);
    }

    // Implement your analytics service here (e.g., PostHog, Mixpanel)
    // Example with PostHog:
    /*
    if (window.posthog) {
      window.posthog.capture(eventName, properties);
    }
    */
  }

  trackPageView(path: string, properties?: Record<string, any>) {
    if (import.meta.env.DEV) {
      console.log('[Page View]', path, properties);
    }

    // Implement page view tracking
    /*
    if (window.posthog) {
      window.posthog.capture('$pageview', {
        $current_url: path,
        ...properties,
      });
    }
    */
  }
}

export const monitoring = new Monitoring();

// React Error Boundary helper
export function logErrorToService(error: Error, errorInfo: React.ErrorInfo) {
  monitoring.captureError(error, {
    component: errorInfo.componentStack,
    errorBoundary: true,
  });
}
